import type {
  TemplateCalendarDto,
  TemplateDecisionClass,
  TemplateDecisionSummaryDto,
  TemplateListItemDto,
} from '@pams/shared';
import { templateStateOf } from '@/lib/statusPresentation';
import { noDecisionFreesATemplate, openDecisionsOf } from './openDecisions';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7C §3] THE SETUP GUIDE'S FIGURES — pure, and nearly all of them somebody else's.
 *
 *  ⚠️ THE DECISION COUNTS ARE THE SERVER'S (`set.decisions`), the figures the decisions page's own class
 *  switch and `DecisionSummary` show — so the guide, the summary and the page a step links to cannot
 *  count one class two ways. Recounting them here from the templates' `waitsOn` would miss the codes a
 *  `waitsOn` leaves out by design (a unit named only as an in-charge's guidance), and the guide would
 *  say "2 sources" above a page saying "Sources and units (3)".
 *
 *  ⚠️ THE TEMPLATES WAITING ARE A DISTINCT COUNT, NEVER A SUM OF THE CLASSES'. One template held back by
 *  a column AND its days and times is one template; the per-class figures overlap, and adding them up
 *  states more templates than the set holds back.
 *
 *  ⚠️ THE PANEL'S OWN ANSWERS ARE THE PANEL'S OWN FUNCTIONS: the "waiting for a policy / for in-charge
 *  roles" markers, and whether any single decision frees a template on its own, come from
 *  `openDecisionsOf` and `noDecisionFreesATemplate` — so the guide and the panel (the dialog behind the
 *  link above the tree, [P7C.1 §2]) cannot disagree. Only what NOTHING else counts is counted here: templates ready and not yet created, drafts
 *  waiting to be published, and festival days with no date this year.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const SETUP_STEPS = ['SCHEDULE', 'FORCE_COLUMN', 'POLICY', 'SOURCE', 'CREATE', 'PUBLISH'] as const;
export type SetupStep = (typeof SETUP_STEPS)[number];

export type ClassFigure = { readonly outstanding: number; readonly templatesAffected: number };

export type SetupFigures = {
  /** Per class, the server's own figures — a class the summary leaves out counts nothing. */
  readonly classes: Readonly<Record<TemplateDecisionClass, ClassFigure>>;
  readonly outstanding: number;
  /** Templates held back by at least one open decision — the server's distinct count. */
  readonly templatesAffected: number;
  /** The panel's markers: templates that will ask for a policy, and for in-charge roles, once they can. */
  readonly waiting: Readonly<Record<'POLICY' | 'IN_CHARGE_ROLE', number>>;
  /** The panel's own condition for "no single decision makes a template ready". */
  readonly noneAlone: boolean;
  /** Ready by the server, and not already created — what "select all ready" picks. */
  readonly ready: number;
  /**
   * Drafts the set's templates produced that are not archived. ⚠️ Every year's: one type is registered
   * today, so it is the year the Publish step names; a second registered year would need this filtered.
   */
  readonly drafts: number;
};

const NONE: ClassFigure = { outstanding: 0, templatesAffected: 0 };

export const setupFiguresOf = (
  summary: TemplateDecisionSummaryDto,
  items: readonly TemplateListItemDto[],
  rareColumns: ReadonlyMap<string, number>,
): SetupFigures => {
  const of = (decisionClass: TemplateDecisionClass): ClassFigure => {
    const row = summary.classes.find((each) => each.class === decisionClass);
    return row ? { outstanding: row.outstanding, templatesAffected: row.templatesAffected } : NONE;
  };
  const open = openDecisionsOf(items, rareColumns);
  const waitingOf = (decisionClass: 'POLICY' | 'IN_CHARGE_ROLE'): number =>
    open.waiting.find((marker) => marker.class === decisionClass)?.templates ?? 0;
  const drafts = new Set(
    items.flatMap((item) =>
      item.produced
        .filter((order) => order.status === 'DRAFT' && order.archivedAt === null)
        .map((order) => order.arrangementId),
    ),
  );
  return {
    classes: {
      SOURCE: of('SOURCE'),
      FORCE_COLUMN: of('FORCE_COLUMN'),
      POLICY: of('POLICY'),
      IN_CHARGE_ROLE: of('IN_CHARGE_ROLE'),
      TOTALS: of('TOTALS'),
      SCHEDULE: of('SCHEDULE'),
      DUTY_POINT: of('DUTY_POINT'),
    },
    outstanding: summary.outstanding,
    templatesAffected: summary.templatesAffected,
    waiting: { POLICY: waitingOf('POLICY'), IN_CHARGE_ROLE: waitingOf('IN_CHARGE_ROLE') },
    noneAlone: noDecisionFreesATemplate(open),
    ready: items.filter((item) => item.readiness.state === 'READY' && templateStateOf(item) === 'READY').length,
    drafts: drafts.size,
  };
};

/** The festival days with no date for the calendar's year, and how many days the set has. */
export const undatedDaysOf = (calendar: Pick<TemplateCalendarDto, 'days'>): { undated: number; days: number } => ({
  undated: calendar.days.filter((day) => day.date === null).length,
  days: calendar.days.length,
});
